import { useEffect, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import QrDisplay from '../components/qr/QrDisplay'
import ScreenBadge from '../components/ScreenBadge'
import { useVisitQr } from '../hooks/useVisitQr'
import { getTenantInfo } from '../lib/storage'
import { formatClock } from '../lib/utils'

const STEPS = ['Open the Agaru app', 'Tap Scan', 'Point at the code']

/**
 * Entrance check-in screen: a screen paired without a room shows the
 * gym-floor check-in code full-screen, big enough to scan from the door.
 * While the gym has floor check-in off it shows `whenOff` (the usual idle
 * screen) and switches over by itself once it's turned on.
 */
export default function CheckInKioskScreen({ whenOff }: { whenOff: ReactNode }) {
  const tenant = getTenantInfo()
  const { qr, off } = useVisitQr()
  const [clock, setClock] = useState(() => formatClock(new Date()))

  useEffect(() => {
    const id = setInterval(() => setClock(formatClock(new Date())), 1000)
    return () => clearInterval(id)
  }, [])

  if (off) return <>{whenOff}</>

  return (
    <div className="w-screen h-screen overflow-hidden relative bg-[#0E1022] flex flex-col">
      <div className="relative z-10 flex items-start justify-between px-16 pt-12">
        <div className="text-white/40 text-sm font-semibold tracking-[0.2em] uppercase">
          {tenant?.name ?? 'Agaru'}
        </div>
        <div className="text-6xl font-black text-white/80 tabular-nums tracking-tight">{clock}</div>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-20">
        <div className="flex items-center gap-24 w-full max-w-[1500px]">
          <div className="flex-1 min-w-0">
            <div className="text-orange-300 text-lg font-semibold tracking-[0.2em] uppercase mb-5">
              Gym floor check-in
            </div>
            <h1 className="text-7xl font-black text-white leading-[1.05]">Training on your own today?</h1>
            <p className="text-white/55 text-3xl mt-6 leading-snug">
              Scan to check in. Your visit counts toward your streak, the gym leaderboard and challenges.
            </p>
            <p dir="rtl" lang="ar" className="text-white/45 text-3xl mt-4 leading-snug">
              تتمرّن لوحدك اليوم؟ امسح الرمز لتسجيل حضورك.
            </p>

            <ol className="flex flex-wrap gap-3 mt-10">
              {STEPS.map((step, i) => (
                <li
                  key={step}
                  className="flex items-center gap-3 rounded-full bg-[#191C33] border border-[#31365A] pl-2 pr-5 py-2"
                >
                  <span className="w-9 h-9 rounded-full bg-[#C93A30] text-white text-lg font-black flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="text-white/80 text-xl font-semibold">{step}</span>
                </li>
              ))}
            </ol>

            <div className="mt-10 h-10 text-2xl text-white/60" aria-live="polite">
              {qr ? <CheckInCount count={qr.checkInsToday} /> : null}
            </div>
          </div>

          <div className="shrink-0 rounded-[32px] bg-[#191C33] border border-[#31365A] p-10">
            <QrDisplay token={qr?.token ?? null} signature={qr?.signature ?? null} expiresAt={qr?.expiresAt ?? null} size={380} />
          </div>
        </div>
      </div>

      <div className="absolute top-5 left-5 z-20">
        <ScreenBadge className="text-white/25 text-[10px]" />
      </div>
    </div>
  )
}

/** "12 members checked in today": the number eases up a step when someone scans. */
function CheckInCount({ count }: { count: number }) {
  if (count === 0) return <span>Be the first to check in today.</span>
  return (
    <span className="inline-flex items-baseline gap-2">
      <span className="relative inline-flex overflow-hidden tabular-nums font-black text-white">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={count}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
          >
            {count}
          </motion.span>
        </AnimatePresence>
      </span>
      <span>{count === 1 ? 'member has checked in today' : 'members have checked in today'}</span>
    </span>
  )
}
